import {
    useActiveClustersWithSubscriptions,
    useCluster,
    usePastClustersWithSubscriptions
} from "in_queue/contexts/ClusterContext";
import {
    useScenariosInCluster,
    useTrackedProjectsInCluster
} from "in_queue/contexts/ProjectDataContext";
import { Cluster, getClusterLabel } from "in_queue/types/clusterType";
import { ScenarioMetadata, ScenarioStatus } from "in_queue/types/scenarioType";
import { useMemo } from "react";
import {
    createClusterItem,
    createProjectItem,
    createScenarioItem,
    SearchItem
} from "./searchItems";

const SCENARIO_STATUS_ORDER: Record<ScenarioStatus, number> = {
    [ScenarioStatus.RunSucceeded]: 0,
    [ScenarioStatus.Running]: 1,
    [ScenarioStatus.RunRequested]: 1,
    [ScenarioStatus.RunSubmitted]: 1,
    [ScenarioStatus.Draft]: 1,
    [ScenarioStatus.RunFailed]: 2,
    [ScenarioStatus.Archived]: 3
};

const sortScenariosByStatus = (
    scenarios: ScenarioMetadata[]
): ScenarioMetadata[] =>
    [...scenarios].sort(
        (a, b) =>
            SCENARIO_STATUS_ORDER[a.status] - SCENARIO_STATUS_ORDER[b.status]
    );

const sortClustersByLabel = (clusters: Cluster[]): Cluster[] =>
    [...clusters].sort((a, b) =>
        getClusterLabel(a).localeCompare(getClusterLabel(b))
    );

export const useSearchPaletteItems = (): "loading" | SearchItem[] => {
    const activeClusters = useActiveClustersWithSubscriptions();
    const pastClusters = usePastClustersWithSubscriptions();
    const cluster = useCluster();
    const trackedProjects = useTrackedProjectsInCluster();
    const scenarios = useScenariosInCluster();

    return useMemo(() => {
        if (trackedProjects === "loading" || scenarios === "loading") {
            return "loading";
        }

        const items: SearchItem[] = [];

        sortClustersByLabel(activeClusters).forEach((cluster) => {
            items.push(createClusterItem({ cluster, clusterStatus: "active" }));
        });

        sortClustersByLabel(pastClusters).forEach((cluster) => {
            items.push(createClusterItem({ cluster, clusterStatus: "past" }));
        });

        trackedProjects.forEach((project) => {
            items.push(createProjectItem({ project, cluster }));
        });

        const firstTrackedProjectId =
            trackedProjects.length > 0
                ? trackedProjects[0].projectId
                : undefined;

        sortScenariosByStatus(scenarios).forEach((scenario) => {
            const navigateProjectId =
                scenario.parentProjectId ?? firstTrackedProjectId;
            if (navigateProjectId) {
                items.push(
                    createScenarioItem({
                        scenario,
                        navigateProjectId,
                        cluster
                    })
                );
            }
        });

        return items;
    }, [activeClusters, pastClusters, cluster, trackedProjects, scenarios]);
};
